import { useMemo } from 'react';

import type { UserRole } from '@/features/courses/useCourse';

export interface QuestionInteractionInput {
  attemptsAllowed?: number | null;
  attemptsUsed: number;
  areSolutionsVisible: boolean;
  // There is an answer on screen, and it is not the one last checked. A check
  // stores the answer it grades, so it need not have been saved (issue #92:
  // Check used to wait for a save). Defaults to hasSavedAnswer.
  hasCheckableAnswer?: boolean;
  hasEarnedMaxPoints?: boolean;
  hasCheckedAtLeastOnce?: boolean;
  hasSavedAnswer?: boolean;
  hasSelectionChangedFromSaved?: boolean;
  hideStatsAndSolutions?: boolean;
  hintSetting?: number | null;
  isPractice?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  // The attempts SETTING is 1: Save only, no Check, no count (see
  // BaseQuestionData.isSingleAttemptMode). A question the option count
  // limited to one check passes false. Defaults to attemptsAllowed === 1.
  isSingleAttemptMode?: boolean;
  role: UserRole;
}

export interface QuestionInteractionState {
  showSaveButton: boolean;
  showCheckButton: boolean;
  isSaveButtonDisabled: boolean;
  isCheckButtonDisabled: boolean;
  showAttemptCount: boolean;
  attemptsUsed: number;
  attemptsAllowed?: number;
  isAnswerLocked: boolean;

  showCorrectAnswer: boolean;
  showSolution: boolean;
  showHints: boolean;
  showEarnedPoints: boolean;
  showCorrectIncorrectFeedback: boolean;

  showMaxPoints: boolean;
  isReviewMode: boolean;
  // True when the question is locked specifically because the student has CHECKED
  // a fully-correct answer (so the UI can explain why submissions have ended).
  isLockedForCorrectAnswer: boolean;
}

export function getQuestionInteractionState(
  input: QuestionInteractionInput,
): QuestionInteractionState {
  const attemptsAllowed =
    typeof input.attemptsAllowed === 'number' && Number.isFinite(input.attemptsAllowed)
      ? input.attemptsAllowed
      : undefined;
  const hideStatsAndSolutions = input.hideStatsAndSolutions ?? false;
  const hasEarnedMaxPoints = input.hasEarnedMaxPoints ?? false;
  const hasSavedAnswer = input.hasSavedAnswer ?? false;
  const hasCheckableAnswer = input.hasCheckableAnswer ?? hasSavedAnswer;
  const hasCheckedAtLeastOnce = input.hasCheckedAtLeastOnce ?? false;
  const hasSelectionChangedFromSaved =
    input.hasSelectionChangedFromSaved ?? !hasSavedAnswer;
  const isPractice = input.isPractice ?? false;
  // Earning max points should only lock the question (and end submissions) once
  // the student has CHECKED and seen the result. Merely SAVING a correct answer
  // must not reveal correctness, lock the inputs, or disable Check.
  const hasRevealedMaxPoints = hasEarnedMaxPoints && hasCheckedAtLeastOnce;
  const isSingleAttemptMode = input.isSingleAttemptMode ?? attemptsAllowed === 1;
  const hasAttemptsRemaining =
    attemptsAllowed === undefined || input.attemptsUsed < attemptsAllowed;
  // A single-attempt question has no Check; its saved answer is revisable until
  // the section closes (the due date). Its one consumed attempt must NOT exhaust
  // interaction — otherwise saving once would lock it and hide the Save button.
  const attemptsExhausted = !isSingleAttemptMode && !hasAttemptsRemaining;
  const isInstructionalRole =
    input.role === 'instructor' || input.role === 'assistant';
  const isReviewMode =
    input.role === 'student' &&
    input.isSectionClosed &&
    input.areSolutionsVisible;

  const showSaveButton =
    input.role === 'student' &&
    input.isSectionOpen &&
    !attemptsExhausted &&
    !isPractice;
  const showCheckButton =
    input.role === 'student' &&
    input.isSectionOpen &&
    hasAttemptsRemaining &&
    !isSingleAttemptMode &&
    !isPractice;

  const showCorrectAnswer =
    !hideStatsAndSolutions &&
    (isInstructionalRole ||
      (input.role === 'student' &&
        input.isSectionClosed &&
        input.areSolutionsVisible));

  // 0 is what the settings dialogs save for "Only with the solutions", and an
  // unset setting reads the same: the hint shows with the solutions. 0 used to
  // show nothing, ever (issue #92).
  const studentHintsVisible =
    (!input.isSectionClosed || input.areSolutionsVisible) &&
    (input.hintSetting === -1 ||
      ((input.hintSetting === undefined ||
        input.hintSetting === null ||
        input.hintSetting === 0) &&
        input.isSectionClosed &&
        input.areSolutionsVisible) ||
      (typeof input.hintSetting === 'number' &&
        input.hintSetting > 0 &&
        input.attemptsUsed >= input.hintSetting));

  const showHints =
    !hideStatsAndSolutions &&
    (isInstructionalRole ||
      (input.role === 'student' && studentHintsVisible));

  const showCorrectIncorrectFeedback =
    input.role === 'student' &&
    !hideStatsAndSolutions &&
    ((hasCheckedAtLeastOnce && input.isSectionOpen) ||
      (input.isSectionClosed && input.areSolutionsVisible));

  return {
    attemptsAllowed,
    attemptsUsed: input.attemptsUsed,
    isAnswerLocked:
      isInstructionalRole ||
      isPractice ||
      input.isSectionClosed ||
      hasRevealedMaxPoints ||
      attemptsExhausted,
    isCheckButtonDisabled: showCheckButton
      ? !hasCheckableAnswer || hasRevealedMaxPoints
      : false,
    isLockedForCorrectAnswer: input.role === 'student' && hasRevealedMaxPoints,
    isSaveButtonDisabled: showSaveButton
      ? !hasSelectionChangedFromSaved || hasRevealedMaxPoints
      : false,
    isReviewMode,
    // Whenever Check is offered with a limit, a limit of 1 included.
    showAttemptCount:
      input.role === 'student' &&
      input.isSectionOpen &&
      !isPractice &&
      attemptsAllowed !== undefined &&
      !isSingleAttemptMode,
    showCheckButton,
    showCorrectAnswer,
    showCorrectIncorrectFeedback,
    showEarnedPoints:
      input.role === 'student' &&
      input.isSectionClosed &&
      !hideStatsAndSolutions,
    showHints,
    showMaxPoints: true,
    showSaveButton,
    showSolution: showCorrectAnswer,
  };
}

export function useQuestionInteractionState(
  input: QuestionInteractionInput,
) {
  return useMemo(
    () => getQuestionInteractionState(input),
    [
      input.attemptsAllowed,
      input.attemptsUsed,
      input.areSolutionsVisible,
      input.hasCheckableAnswer,
      input.hasEarnedMaxPoints,
      input.hasCheckedAtLeastOnce,
      input.hasSavedAnswer,
      input.hasSelectionChangedFromSaved,
      input.hideStatsAndSolutions,
      input.hintSetting,
      input.isPractice,
      input.isSectionClosed,
      input.isSectionOpen,
      input.isSingleAttemptMode,
      input.role,
    ],
  );
}
